import React, { useEffect, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { configureServices } from './services';
import { useUser } from './contexts/userContext';
import { getAuth, onAuthStateChanged, Unsubscribe } from 'firebase/auth';
import { firebaseApp } from './firebase-config';
import { authService } from './services/auth.service';
import { PrivateRoute } from './components/PrivateRoute';
import Payment from './pages/Payment';
import AuthAction from './pages/AuthAction';
import Landing from './pages/Landing';
import About from './pages/About';
import Product from './pages/Product';
import Faq from './pages/Faq';
import Contact from './pages/Contact';
import LoginForm from './pages/Authentication/LoginForm';
import RegisterForm from './pages/Authentication/RegisterForm';
import Feedback from './pages/Feedback';
import PersonalInformation from './pages/Account/PersonalInformation';
import SubscriptionPage from './pages/Account/SubscriptionPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import Subscribed from './pages/Subscribed';
import DownloadBeta from './pages/DownloadBeta';
import NotFound from './pages/404';
import FeedbackReport from './pages/FeedbackReport';
import Admin from './pages/Admin';
import WaitList from './pages/WaitList';
import DemoSubscriptions from './pages/Invites';
import ForgotPasswordForm from './pages/Authentication/ForgotPasswordForm';
import './App.css';


configureServices(process.env.REACT_APP_BACKEND_URL || '');

const ScrollToTop = (props: any) => {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  return <>{props.children}</>;
};

function App() {
  const auth = getAuth(firebaseApp);
  const [dataLoaded, setDataLoaded] = useState(false);

  const {
    state: { isLoggedIn },
    dispatch
  } = useUser();

  let unsubscribe: Unsubscribe;

  const authentication = getAuth(firebaseApp);

  const checkFeedback = async () => {
    if (authentication.currentUser === null) {
      return;
    }
    const response = await authService.feedbackSentCheck(authentication.currentUser.uid);

    if (response.responseObject === true) {
      dispatch({ type: 'set_feedback', payload: true });
    }

    if (response.responseObject === false) {
      dispatch({
        type: 'set_feedback',
        payload: false
      });
    }
  };

  const checkSubscription = async () => {
    const response = await authService.checkSubscription();

    if (response.responseObject.activeSubscription === true) {
      dispatch({ type: 'set_subscribed', payload: true });
      dispatch({
        type: 'set_subscription_id',
        payload: response.responseObject.stripeInfo.stripeId
      });
      dispatch({ type: 'set_is_subscribed', payload: true });
    }

    if (response.responseObject.activeSubscription === false) {
      dispatch({
        type: 'set_subscribed',
        payload: false
      });
      dispatch({
        type: 'set_is_subscribed',
        payload: false
      });
    }

    if (response.responseObject.isTrial === true) {
      dispatch({
        type: 'set_trial',
        payload: true
      });
    } else {
      dispatch({
        type: 'set_trial',
        payload: false
      });
    }

    if (response.responseObject.requestedToBeCancelled) {
      dispatch({
        type: 'set_request_cancelled',
        payload: true
      });
    }
  };
  const checkUserStatus = async () => {
    try {
      const {
        responseObject: { isAdmin = false, hasBetaAccess = false }
      } = await authService.getUserStatus();

      dispatch({ type: 'set_user_status', payload: { isAdmin, hasBetaAccess } });
    } catch (e) {
      console.error(e);
      dispatch({ type: 'set_user_status', payload: { isAdmin: false, hasBetaAccess: false } });
    }
  };

  useEffect(() => {
    if (isLoggedIn === false) {
      // Firebase auth listener
      unsubscribe = onAuthStateChanged(auth, (user) => {
        if (user) {
          dispatch({
            type: 'set_logged',
            payload: {
              isLoggedIn: true
            }
          });
          unsubscribe();
        }
        setDataLoaded(true);
      });
    } else {
      checkUserStatus();
      checkSubscription();
      checkFeedback();
    }
  }, [isLoggedIn]);

  if (!dataLoaded) {
    return null;
  }

  return (
    <ScrollToTop>
      <Routes>
        <Route path="/auth-action" element={<AuthAction />} />
        <Route path="/payment/:paymentType" element={<Payment />} />
        <Route path="/" element={<Landing />} />
        <Route path="/product" element={<Product />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/policy" element={<PrivacyPolicy />} />
        <Route path="/tc" element={<TermsAndConditions />} />
        <Route path="/feedback" element={<Feedback />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/account" element={<PersonalInformation />} />
        <Route path="/subscription" element={<SubscriptionPage />} />
        <Route path="/signin" element={<LoginForm />} />
        <Route path="/reset-password" element={<ForgotPasswordForm />} />
        <Route path="/signup" element={<RegisterForm />} />
        <Route path="/feedback-report" element={<FeedbackReport />} />
        <Route path="/wait-list" element={<WaitList />} />
        <Route path="/download" element={<DownloadBeta />} />
        <Route path="/downloads" element={<DownloadBeta />} />
        <Route path="/success" element={<Subscribed />} />
        <Route path="/reports-admin" element={<PrivateRoute><Admin /></PrivateRoute>} />
        <Route path="/invites-admin" element={<PrivateRoute><DemoSubscriptions /></PrivateRoute>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </ScrollToTop>
  );
}

export default App;
